import React, { useCallback, useEffect, useMemo, useRef, useState, Suspense } from "react";
import { usePalette } from "@/lib/palette";
import cytoscape from "cytoscape";
import fcose from "cytoscape-fcose";
import CytoscapeComponent from "react-cytoscapejs";
import { Search, Filter, Maximize2, Layers, RefreshCcw, Radar, Loader2, X, Camera, FileDown } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TID } from "@/lib/testIds";
import CanvasBoundary from "@/components/fx/CanvasBoundary";

// fCoSE: the industry-standard force-directed layout (spectral init + CoSE
// refinement) — dramatically better cluster separation than plain cose.
try { cytoscape.use(fcose); } catch (e) { /* already registered */ }

const GraphBackdrop3D = React.lazy(() => import("@/components/fx/GraphBackdrop3D"));

const TYPE_COLORS_BY_THEME = (P) => ({
    artifact: P.brand,
    program: P.accent,
    auth_object: P.neutral,
    tcode: "#FFD166",
    odata: P.warn,
    finding: P.danger,
    recommendation: "#A78BFA",
});

const TYPE_SHAPES = {
    artifact: "round-rectangle",
    program: "round-hexagon",
    auth_object: "ellipse",
    tcode: "round-tag",
    odata: "round-diamond",
    finding: "round-octagon",
    recommendation: "star",
};

const ALL_TYPES = ["artifact", "program", "auth_object", "tcode", "odata", "finding", "recommendation"];

const LAYOUTS = {
    fcose: {
        label: "Force (fCoSE)",
        def: { name: "fcose", quality: "proof", animate: false, randomize: true, nodeRepulsion: 7500, idealEdgeLength: 95, edgeElasticity: 0.45, gravity: 0.25, gravityRange: 3.8, packComponents: true, padding: 36 },
    },
    concentric: {
        label: "Concentric (by degree)",
        def: { name: "concentric", animate: false, padding: 36, minNodeSpacing: 28, concentric: (n) => n.degree(false), levelWidth: () => 2 },
    },
    breadthfirst: {
        label: "Hierarchy",
        def: { name: "breadthfirst", animate: false, padding: 36, directed: true, spacingFactor: 1.15 },
    },
    grid: {
        label: "Grid",
        def: { name: "grid", animate: false, padding: 36 },
    },
};

/** Edges point from consumer → resource (a program *checks* an auth object, a
 * t-code *requires* it), so "what relies on this" is the incoming direction. */
const DIRECTIONS = [
    { id: "both", label: "Both ways" },
    { id: "upstream", label: "Dependents · what relies on it" },
    { id: "downstream", label: "Dependencies · what it relies on" },
];

const buildStylesheet = (P, TYPE_COLORS) => [
    {
        selector: "node",
        style: {
            "background-color": (ele) => TYPE_COLORS[ele.data("type")] || P.accent,
            "background-opacity": 0.92,
            // Cytoscape's colour parser rejects 8-digit hex, so translucency has
            // to ride on border-opacity rather than an alpha suffix.
            "border-color": (ele) => TYPE_COLORS[ele.data("type")] || P.accent,
            "border-opacity": 0.4,
            "border-width": 1.5,
            // Soft halo glow in the node's own colour — the signature
            // "premium SOC" treatment.
            "underlay-color": (ele) => TYPE_COLORS[ele.data("type")] || P.accent,
            "underlay-opacity": 0.14,
            "underlay-padding": 7,
            "underlay-shape": "ellipse",
            "label": "data(label)",
            "color": "#E2E8F0",
            "font-family": "IBM Plex Mono",
            "font-size": 9,
            "text-valign": "bottom",
            "text-margin-y": 7,
            "text-outline-color": P.canvas,
            "text-outline-width": 2,
            "text-max-width": 120,
            "text-wrap": "ellipsis",
            // Labels fade out when the canvas is zoomed out far enough that they
            // would collide — the standard cure for dense-graph label soup.
            "min-zoomed-font-size": 9,
            // Hubs read bigger: size scales with degree.
            "width": (ele) => 18 + Math.min(ele.degree(false) * 1.6, 16),
            "height": (ele) => 18 + Math.min(ele.degree(false) * 1.6, 16),
            "shape": (ele) => TYPE_SHAPES[ele.data("type")] || "ellipse",
            "overlay-opacity": 0,
            "transition-property": "opacity",
            "transition-duration": "0.15s",
        },
    },
    {
        selector: "node:selected",
        style: {
            "border-color": P.accent,
            "border-opacity": 1,
            "border-width": 3,
            "underlay-opacity": 0.32,
            "underlay-padding": 12,
            "font-size": 10,
            "text-max-width": 180,
        },
    },
    {
        selector: "edge",
        style: {
            "width": 1.2,
            "curve-style": "bezier",
            "line-color": "rgba(148,163,184,0.22)",
            "line-cap": "round",
            "target-arrow-color": "rgba(148,163,184,0.4)",
            "target-arrow-shape": "vee",
            "arrow-scale": 0.7,
            // Relationship labels are hidden until the edge is highlighted —
            // a clutter-free canvas is what makes big graphs readable.
            "label": "data(label)",
            "font-size": 8,
            "color": P.accent,
            "font-family": "IBM Plex Mono",
            "text-rotation": "autorotate",
            "text-opacity": 0,
            "text-background-opacity": 0.75,
            "text-background-color": P.canvas,
            "text-background-padding": 2,
            "transition-property": "opacity, line-color",
            "transition-duration": "0.15s",
        },
    },
    {
        selector: "edge:selected",
        style: { "line-color": P.accent, "width": 2, "text-opacity": 1 },
    },
    // ---- neighborhood focus (tap a node → its ego-network lights up) ----
    {
        selector: ".faded",
        style: { "opacity": 0.13, "text-opacity": 0 },
    },
    {
        selector: "edge.hl",
        style: {
            "line-color": P.accent,
            "line-opacity": 0.8,
            "target-arrow-color": P.accent,
            "width": 2,
            "text-opacity": 1,
        },
    },
    // ---- impact analysis (blast radius) overlay ----
    {
        selector: ".dimmed",
        style: { "opacity": 0.1, "text-opacity": 0 },
    },
    {
        selector: "node.impacted",
        style: { "border-width": 2.5, "border-color": P.warn, "border-opacity": 1, "underlay-color": P.warn, "underlay-opacity": 0.22 },
    },
    {
        selector: "edge.impacted",
        style: { "line-color": P.warn, "line-opacity": 0.67, "width": 2, "target-arrow-color": P.warn },
    },
    {
        selector: "node.impact-root",
        style: {
            "border-width": 4,
            "border-color": P.danger,
            "border-opacity": 1,
            "underlay-color": P.danger,
            "underlay-opacity": 0.3,
            "underlay-padding": 14,
            "width": 36,
            "height": 36,
            "font-size": 11,
        },
    },
];

export default function CytoGraph({ workspaceId, height = 600, appliedView = null, onStateChange = null }) {
    const P = usePalette();
    const TYPE_COLORS = useMemo(() => TYPE_COLORS_BY_THEME(P), [P]);
    const stylesheet = useMemo(() => buildStylesheet(P, TYPE_COLORS), [P, TYPE_COLORS]);
    const [data, setData] = useState({ nodes: [], edges: [] });
    const [filter, setFilter] = useState(new Set(ALL_TYPES));
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState(null);
    const [depth, setDepth] = useState(2);
    const [direction, setDirection] = useState("both");
    const [impact, setImpact] = useState(null);
    const [impactLoading, setImpactLoading] = useState(false);
    // Distinguishes "still fetching" from "genuinely empty" so the canvas never
    // tells an already-analysed workspace to go and run an analysis.
    const [graphLoading, setGraphLoading] = useState(true);
    const [layoutId, setLayoutId] = useState("fcose");
    const [briefLoading, setBriefLoading] = useState(false);
    const cyRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            const cy = cyRef.current;
            if (cy && !cy.destroyed?.()) {
                try {
                    cy.stop(true, true);
                    cy.removeAllListeners();
                } catch (e) {
                    console.debug("Cytoscape cleanup ignored", e);
                }
            }
            cyRef.current = null;
        };
    }, []);

    const load = useCallback(async () => {
        if (!workspaceId) return;
        setGraphLoading(true);
        try {
            const { data: resp } = await api.get(`/workspaces/${workspaceId}/graph`);
            if (mountedRef.current) setData(resp);
        } catch (e) {
            console.warn("Knowledge graph fetch failed", e);
            if (mountedRef.current) setData({ nodes: [], edges: [] });
        } finally {
            if (mountedRef.current) setGraphLoading(false);
        }
    }, [workspaceId]);

    useEffect(() => { load(); }, [load]);

    const elements = useMemo(() => {
        const q = query.toLowerCase();
        const visibleNodes = (data.nodes || []).filter(
            (n) => filter.has(n.type) && (!q || (n.label || "").toLowerCase().includes(q) || (n.id || "").toLowerCase().includes(q))
        );
        const nodeIds = new Set(visibleNodes.map((n) => n.id));
        const visibleEdges = (data.edges || []).filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));
        return [
            ...visibleNodes.map((n) => ({ data: { id: n.id, label: n.label, type: n.type, props: n.properties } })),
            ...visibleEdges.map((e) => ({ data: { id: e.id, source: e.source, target: e.target, label: e.relation } })),
        ];
    }, [data, filter, query]);

    // Sync elements into the live cy instance without remounting the component
    useEffect(() => {
        const cy = cyRef.current;
        if (!cy || cy.destroyed?.()) return;
        try {
            cy.batch(() => {
                cy.elements().remove();
                cy.add(elements);
            });
            cy.layout(LAYOUTS[layoutId].def).run();
        } catch (e) {
            console.debug("Cytoscape sync skipped", e);
        }
    }, [elements, layoutId]);

    // Ego-network focus: tapping a node fades everything outside its direct
    // neighborhood and reveals the relationship labels on its edges.
    // Suspended while an impact overlay is active (that takes precedence).
    useEffect(() => {
        const cy = cyRef.current;
        if (!cy || cy.destroyed?.()) return;
        try {
            cy.batch(() => {
                cy.elements().removeClass("faded hl");
                if (!selected || impact) return;
                const ele = cy.getElementById(selected.id);
                if (!ele || !ele.length) return;
                const hood = ele.closedNeighborhood();
                cy.elements().not(hood).addClass("faded");
                hood.edges().addClass("hl");
            });
        } catch (e) {
            console.debug("Neighborhood focus skipped", e);
        }
    }, [selected, impact, elements]);

    // Paint the blast radius: everything outside the impacted set is dimmed.
    useEffect(() => {
        const cy = cyRef.current;
        if (!cy || cy.destroyed?.()) return;
        try {
            cy.batch(() => {
                cy.elements().removeClass("dimmed impacted impact-root");
                if (!impact) return;
                cy.nodes().forEach((n) => n.addClass(impact.ids.has(n.id()) ? "impacted" : "dimmed"));
                cy.edges().forEach((e) => {
                    const inside = impact.ids.has(e.source().id()) && impact.ids.has(e.target().id());
                    e.addClass(inside ? "impacted" : "dimmed");
                });
                cy.getElementById(impact.rootId).removeClass("impacted").addClass("impact-root");
            });
        } catch (e) {
            console.debug("Impact overlay skipped", e);
        }
    }, [impact, elements]);

    // Report the live view state up so it can be saved as a named view.
    useEffect(() => {
        if (!onStateChange) return;
        onStateChange({
            types: Array.from(filter),
            query,
            impact: impact ? { node_id: impact.rootId, depth: impact.depth, direction: impact.direction } : null,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, query, impact]);

    const runImpact = useCallback(async (nodeId, d = depth, dir = direction) => {
        if (!workspaceId || !nodeId) return;
        setImpactLoading(true);
        try {
            const { data: resp } = await api.post(`/workspaces/${workspaceId}/graph/impact`, {
                node_id: nodeId, depth: Number(d), direction: dir,
            });
            if (!mountedRef.current) return;
            setImpact({
                rootId: resp.root.id,
                rootLabel: resp.root.label,
                depth: resp.depth,
                direction: resp.direction,
                ids: new Set(resp.nodes.map((n) => n.id)),
                hops: resp.hops,
                summary: resp.summary,
            });
        } catch (e) {
            console.warn("Impact analysis failed", e);
            toast.error("Impact analysis failed");
        } finally {
            if (mountedRef.current) setImpactLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspaceId, depth, direction]);

    // Restore a saved view (filters + search + impact focus) when one is opened.
    useEffect(() => {
        if (!appliedView) return;
        setFilter(new Set(appliedView.types?.length ? appliedView.types : ALL_TYPES));
        setQuery(appliedView.query || "");
        if (appliedView.impact?.node_id) {
            setDepth(appliedView.impact.depth || 2);
            setDirection(appliedView.impact.direction || "both");
            runImpact(appliedView.impact.node_id, appliedView.impact.depth || 2, appliedView.impact.direction || "both");
        } else {
            setImpact(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedView?.key]);

    const handleCy = useCallback((cy) => {
        if (cyRef.current === cy) return;
        cyRef.current = cy;
        if (!cy || cy.destroyed?.()) return;
        cy.on("tap", "node", (evt) => {
            if (!mountedRef.current) return;
            const n = evt.target;
            setSelected({ id: n.id(), label: n.data("label"), type: n.data("type"), props: n.data("props") });
        });
        cy.on("tap", (evt) => {
            if (!mountedRef.current) return;
            if (evt.target === cy) setSelected(null);
        });
    }, []);

    const toggleType = (t) => {
        setFilter((prev) => {
            const next = new Set(prev);
            if (next.has(t)) next.delete(t); else next.add(t);
            return next;
        });
    };

    /** Search hits are pickable — selecting one opens the node panel (and its
     * impact controls) without hunting for the dot on the canvas. */
    const matches = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (q.length < 2) return [];
        return (data.nodes || [])
            .filter((n) => filter.has(n.type) && ((n.label || "").toLowerCase().includes(q) || (n.id || "").toLowerCase().includes(q)))
            .slice(0, 6);
    }, [data.nodes, filter, query]);

    const selectNode = (n) => {
        setSelected({ id: n.id, label: n.label, type: n.type, props: n.properties });
        try {
            const cy = cyRef.current;
            const ele = cy?.getElementById(n.id);
            if (ele && ele.length) { cy.center(ele); ele.select(); }
        } catch (e) { console.debug("center skipped", e); }
    };

    const fit = () => { try { cyRef.current?.fit(null, 30); } catch (e) { console.debug("fit skipped", e); } };
    const relayout = () => { try { cyRef.current?.layout(LAYOUTS[layoutId].def).run(); } catch (e) { console.debug("layout skipped", e); } };

    /** High-res PNG snapshot of the live canvas (includes any impact overlay). */
    const exportPng = () => {
        try {
            const cy = cyRef.current;
            if (!cy || cy.destroyed?.()) return;
            const uri = cy.png({ full: true, scale: 2, bg: P.canvas });
            const a = document.createElement("a");
            a.href = uri;
            a.download = impact ? `change_impact_${(impact.rootLabel || "graph").replace(/[^a-z0-9_-]+/gi, "_").slice(0, 40)}.png` : "knowledge_graph.png";
            document.body.appendChild(a);
            a.click();
            a.remove();
            toast.success("Graph PNG exported");
        } catch (e) {
            toast.error("PNG export failed");
        }
    };

    /** Change Impact Brief — backend-rendered PDF of the current blast radius. */
    const exportBrief = async () => {
        if (!impact || !workspaceId) return;
        setBriefLoading(true);
        try {
            const res = await api.post(
                `/workspaces/${workspaceId}/graph/impact/brief`,
                { node_id: impact.rootId, depth: impact.depth, direction: impact.direction },
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "change_impact_brief.pdf";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Change Impact Brief exported");
        } catch (e) {
            toast.error("Impact Brief export failed");
        } finally {
            if (mountedRef.current) setBriefLoading(false);
        }
    };

    return (
        <div className="cy-shell" data-testid={TID.graphCanvas}>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5 flex-wrap" data-testid={TID.graphFilter}>
                <div className="flex items-center gap-2 panel px-3 py-1.5 min-w-[240px] relative">
                    <Search size={12} className="text-slate-500" />
                    <input
                        data-testid={TID.graphSearchInput}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search nodes…"
                        className="bg-transparent text-xs flex-1 outline-none text-slate-200 placeholder:text-slate-500"
                    />
                    {matches.length > 0 && (
                        <div className="absolute left-0 right-0 top-full mt-1 z-20 hud-glass rounded-md p-1" data-testid={TID.graphSearchResults}>
                            {matches.map((n, i) => (
                                <button
                                    key={n.id}
                                    data-testid={TID.graphSearchResult(i)}
                                    onClick={() => selectNode(n)}
                                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-white/[0.06]"
                                >
                                    <span className="dot shrink-0" style={{ background: TYPE_COLORS[n.type] }} />
                                    <span className="text-[11px] text-slate-200 truncate flex-1">{n.label}</span>
                                    <span className="text-[9px] font-mono uppercase text-slate-500">{n.type.replace("_", " ")}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                    <Filter size={12} className="text-slate-500" />
                    {ALL_TYPES.map((t) => (
                        <button
                            key={t}
                            type="button"
                            onClick={() => toggleType(t)}
                            className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm border transition-colors ${
                                filter.has(t)
                                    ? "border-cyan-500/40 text-cyan-300 bg-cyan-500/10"
                                    : "border-white/10 text-slate-500"
                            }`}
                            style={{ borderColor: filter.has(t) ? TYPE_COLORS[t] + "55" : undefined, color: filter.has(t) ? TYPE_COLORS[t] : undefined }}
                        >
                            <span className="dot mr-1.5" style={{ background: TYPE_COLORS[t] }} />
                            {t.replace("_", " ")}
                        </button>
                    ))}
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <select
                        data-testid="graph-layout-select"
                        value={layoutId}
                        onChange={(e) => setLayoutId(e.target.value)}
                        className="field py-1 text-[11px]"
                        title="Layout algorithm"
                    >
                        {Object.entries(LAYOUTS).map(([id, l]) => <option key={id} value={id}>{l.label}</option>)}
                    </select>
                    <button type="button" onClick={fit} className="btn-ghost flex items-center gap-1">
                        <Maximize2 size={11} /> Fit
                    </button>
                    <button type="button" onClick={relayout} className="btn-ghost flex items-center gap-1">
                        <Layers size={11} /> Layout
                    </button>
                    <button type="button" data-testid="graph-export-png" onClick={exportPng} className="btn-ghost flex items-center gap-1" title="Download a high-res PNG snapshot">
                        <Camera size={11} /> PNG
                    </button>
                    <button type="button" onClick={load} className="btn-ghost flex items-center gap-1">
                        <RefreshCcw size={11} /> Reload
                    </button>
                </div>
            </div>

            <div className="relative" style={{ height }}>
                {/* 3D parallax depth backdrop */}
                <div className="absolute inset-0 z-0 pointer-events-none">
                    <div className="absolute inset-0" style={{
                        background: "radial-gradient(ellipse at center, rgb(var(--accent-2-rgb) / 0.12), transparent 65%)",
                    }} />
                    <CanvasBoundary fallback={null}>
                        <Suspense fallback={null}>
                            <GraphBackdrop3D />
                        </Suspense>
                    </CanvasBoundary>
                </div>
                {graphLoading && data.nodes.length === 0 ? (
                    <div
                        data-testid="graph-loading-state"
                        className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 font-mono text-xs z-[1]"
                    >
                        <Layers size={28} className="mb-3 text-slate-700 animate-pulse" />
                        Assembling the knowledge graph…
                    </div>
                ) : data.nodes.length === 0 ? (
                    <div
                        data-testid="graph-empty-state"
                        className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 font-mono text-xs z-[1]"
                    >
                        <Layers size={28} className="mb-3 text-slate-700" />
                        Run an analysis to populate the knowledge graph.
                    </div>
                ) : (
                    <div className="relative z-[1] w-full h-full">
                        <CytoscapeComponent
                            cy={handleCy}
                            elements={[]}
                            stylesheet={stylesheet}
                            layout={LAYOUTS.fcose.def}
                            style={{ width: "100%", height: "100%" }}
                        />
                    </div>
                )}
                {selected && (
                    <div className="absolute top-4 right-4 w-80 z-10">
                    <div className="panel-elevated p-4" data-testid={TID.graphNodePanel}>
                        <div className="label-eyebrow mb-1" style={{ color: TYPE_COLORS[selected.type] }}>{selected.type}</div>
                        <div className="font-display text-base text-white truncate">{selected.label}</div>
                        <div className="font-mono text-[10px] text-slate-500 mt-1 break-all">{selected.id}</div>
                        {selected.props && Object.keys(selected.props).length > 0 && (
                            <div className="mt-3 space-y-1">
                                {Object.entries(selected.props).map(([k, v]) => (
                                    <div key={k} className="flex justify-between text-[11px]">
                                        <span className="font-mono text-slate-500">{k}</span>
                                        <span className="text-slate-300">{String(v)}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="mt-4 pt-3 border-t border-white/10">
                            <div className="label-eyebrow mb-2 flex items-center gap-1.5">
                                <Radar size={11} strokeWidth={1.5} /> Impact analysis
                            </div>
                            <select
                                data-testid={TID.graphImpactDirection}
                                value={direction}
                                onChange={(e) => setDirection(e.target.value)}
                                className="field py-1 text-[11px] w-full"
                            >
                                {DIRECTIONS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                            </select>
                            <select
                                data-testid={TID.graphImpactDepth}
                                value={depth}
                                onChange={(e) => setDepth(Number(e.target.value))}
                                className="field py-1 text-[11px] w-full mt-2"
                            >
                                {[1, 2, 3, 4, 5].map((d) => <option key={d} value={d}>{`${d} hop${d > 1 ? "s" : ""} deep`}</option>)}
                            </select>
                            <button
                                data-testid={TID.graphImpactRun}
                                onClick={() => runImpact(selected.id)}
                                disabled={impactLoading}
                                className="btn-primary w-full mt-2 flex items-center justify-center gap-1.5 disabled:opacity-50"
                            >
                                {impactLoading ? <Loader2 size={11} className="animate-spin" /> : <Radar size={11} />}
                                Trace blast radius
                            </button>
                        </div>
                    </div>
                    </div>
                )}

                {impact && (
                    <div className="absolute top-4 left-4 w-72 z-10">
                    <div className="panel-elevated p-4" data-testid={TID.graphImpactPanel}>
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                                <div className="label-eyebrow mb-1">Blast radius</div>
                                <div className="font-display text-sm text-white truncate">{impact.rootLabel}</div>
                            </div>
                            <button
                                data-testid={TID.graphImpactClear}
                                onClick={() => setImpact(null)}
                                className="text-slate-500 hover:text-rose-400 transition-colors shrink-0"
                                title="Clear impact overlay"
                            >
                                <X size={13} />
                            </button>
                        </div>
                        <div className="mt-2 flex items-baseline gap-2">
                            <span className="font-display text-2xl text-cyan-300" data-testid={TID.graphImpactCount}>
                                {impact.summary.impacted_nodes}
                            </span>
                            <span className="text-[11px] text-slate-400">impacted nodes · {impact.summary.impacted_edges} edges</span>
                        </div>
                        <div className="mt-1 text-[10px] font-mono uppercase tracking-wider text-slate-500">
                            {DIRECTIONS.find((d) => d.id === impact.direction)?.label} · {impact.depth} hop max
                        </div>

                        <div className="mt-3 space-y-1.5">
                            {impact.hops.map((h) => (
                                <div key={h.depth} className="flex items-center gap-2 text-[11px]">
                                    <span className="font-mono text-slate-500 w-10">hop {h.depth}</span>
                                    <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                                        <div
                                            className="h-full rounded-full"
                                            style={{
                                                width: `${Math.min(100, (h.nodes.length / Math.max(1, impact.summary.impacted_nodes)) * 100)}%`,
                                                background: P.warn,
                                            }}
                                        />
                                    </div>
                                    <span className="text-slate-300 w-6 text-right">{h.nodes.length}</span>
                                </div>
                            ))}
                        </div>

                        {Object.keys(impact.summary.by_type || {}).length > 0 && (
                            <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap gap-1.5">
                                {Object.entries(impact.summary.by_type).map(([t, n]) => (
                                    <span
                                        key={t}
                                        className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border"
                                        style={{ color: TYPE_COLORS[t], borderColor: (TYPE_COLORS[t] || "#888") + "55" }}
                                    >
                                        {t.replace("_", " ")} {n}
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Change Impact Brief — client-deck-ready exports */}
                        <div className="mt-3 pt-3 border-t border-white/10">
                            <div className="label-eyebrow mb-2">Change Impact Brief</div>
                            <div className="flex items-center gap-2">
                                <button
                                    data-testid="graph-impact-brief-pdf"
                                    onClick={exportBrief}
                                    disabled={briefLoading}
                                    className="btn-primary flex-1 flex items-center justify-center gap-1.5 disabled:opacity-50"
                                    title="Root object, hop table, affected objects & timestamp as a presentation-grade PDF"
                                >
                                    {briefLoading ? <Loader2 size={11} className="animate-spin" /> : <FileDown size={11} />}
                                    Brief PDF
                                </button>
                                <button
                                    data-testid="graph-impact-brief-png"
                                    onClick={exportPng}
                                    className="btn-ghost flex-1 flex items-center justify-center gap-1.5"
                                    title="High-res PNG snapshot of the highlighted blast radius"
                                >
                                    <Camera size={11} /> PNG
                                </button>
                            </div>
                        </div>
                    </div>
                    </div>
                )}
            </div>
            <div className="px-4 py-2 border-t border-white/5 flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-slate-500">
                <span>{data.nodes.length} nodes · {data.edges.length} edges</span>
                <span className="text-cyan-400/70">Authorization Knowledge Graph · AKG</span>
            </div>
        </div>
    );
}
